/* Almila main menu (snippets/almila-nav.liquid, snippets/almila-menu-drawer.liquid; styles in almila-menu.css).
   Desktop: each link with a panel has a chevron button (disclosure). Each panel is moved into its link's list item,
   so Tab goes from the button into the open panel and on to the next link. Click, Enter or Space toggles it; a
   mouse opens it after a short pause over the link and closes it shortly after leaving the link and its panel;
   Escape, a click on the dimmed page or focus moving elsewhere closes it.
   Drawer (the theme opens and closes it, assets/app.js): a row with a panel opens it as a sub-view; the back
   button returns to the list. Opening the drawer again starts at the list. */
(() => {
  const nav = document.querySelector('[data-almila-nav]');
  if (nav) {
    const items = [...nav.querySelectorAll('[data-almila-nav-item]')];
    const OPEN_DELAY = 120;
    const CLOSE_DELAY = 220;
    let current = null;
    let timer = 0;

    const parts = item => {
      const button = item.querySelector('.almila-nav__toggle');
      return { button, panel: document.getElementById(button.getAttribute('aria-controls')) };
    };
    const close = () => {
      if (!current) return;
      const { button, panel } = parts(current);
      button.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      current.classList.remove('is-open');
      nav.classList.remove('is-open');
      current = null;
    };
    const open = item => {
      if (current === item) return;
      close();
      const { button, panel } = parts(item);
      button.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      item.classList.add('is-open');
      nav.classList.add('is-open');
      current = item;
    };
    // A panel's photos load lazily; they start loading as soon as the pointer or focus reaches its link.
    const warm = item => {
      const { panel } = parts(item);
      if (panel) panel.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
    };
    const later = (fn, delay) => {
      clearTimeout(timer);
      timer = setTimeout(fn, delay);
    };

    items.forEach(item => {
      const { button, panel } = parts(item);
      if (!panel) return;
      item.appendChild(panel);
      button.addEventListener('click', () => {
        clearTimeout(timer);
        if (current === item) close();
        else open(item);
      });
      item.addEventListener('pointerenter', event => {
        warm(item);
        if (event.pointerType === 'mouse') later(() => open(item), current ? 0 : OPEN_DELAY);
      });
      item.addEventListener('focusin', () => warm(item));
      item.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse') later(close, CLOSE_DELAY);
      });
    });

    // Rooms panels: the card shows the room under the mouse or keyboard focus (a tap opens the room, so touch leaves
    // the card on the newest room). The photo comes from the tile's src, at the widths of the card's own srcset; the
    // card changes once the new photo is decoded, so it never shows an empty frame.
    nav.querySelectorAll('[data-almila-menu-card]').forEach(card => {
      const panel = card.closest('[data-almila-menu-panel]');
      const rooms = [...panel.querySelectorAll('[data-almila-menu-room]')];
      const photo = card.querySelector('img');
      const widths = photo ? photo.srcset.split(',').map(candidate => candidate.trim().split(/\s+/)[1]) : [];
      const kind = card.querySelector('[data-card-kind]');
      let wanted = rooms.find(room => room.classList.contains('is-shown'));

      const srcsetFor = src => widths.map(width => {
        const url = new URL(src, location.href);
        url.searchParams.set('width', parseInt(width, 10));
        return `${url.href} ${width}`;
      }).join(', ');
      const apply = (room, src, srcset) => {
        if (wanted !== room) return;
        if (photo) photo.hidden = !src;
        if (src) {
          photo.srcset = srcset;
          photo.src = src;
        }
        card.href = room.href;
        kind.textContent = room.dataset.kind;
        kind.hidden = !room.dataset.kind;
        card.querySelector('[data-card-name]').textContent = room.dataset.name;
        card.querySelector('[data-card-products]').textContent = room.querySelector('.almila-menu-room__count')?.textContent || '';
        rooms.forEach(other => other.classList.toggle('is-shown', other === room));
      };
      const show = room => {
        if (wanted === room) return;
        wanted = room;
        const src = photo && room.querySelector('img')?.getAttribute('src');
        if (!src) return apply(room);
        const srcset = srcsetFor(src);
        const next = new Image();
        next.sizes = photo.sizes;
        next.srcset = srcset;
        next.src = src;
        next.decode().catch(() => {}).then(() => apply(room, src, srcset));
      };

      rooms.forEach(room => {
        room.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') show(room);
        });
        room.addEventListener('focus', () => show(room));
      });
    });

    // The dimmed page below an open panel is the nav's ::after.
    nav.addEventListener('click', event => {
      if (event.target === nav) close();
    });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !current) return;
      const { button } = parts(current);
      close();
      button.focus();
    });
    document.addEventListener('focusin', event => {
      if (!current) return;
      const { panel } = parts(current);
      if (!current.contains(event.target) && !panel.contains(event.target)) close();
    });
    document.addEventListener('click', event => {
      if (current && !nav.contains(event.target)) close();
    });
  }

  const drawer = document.querySelector('[data-almila-drawer]');
  if (drawer) {
    const root = drawer.querySelector('[data-almila-drawer-root]');
    const brand = drawer.querySelector('[data-almila-drawer-brand]');
    const back = drawer.querySelector('[data-almila-drawer-back]');
    const title = drawer.querySelector('[data-almila-drawer-title]');
    let opener = null;

    const showRoot = focus => {
      if (!opener) return;
      const panel = document.getElementById(opener.getAttribute('aria-controls'));
      if (panel) panel.hidden = true;
      opener.setAttribute('aria-expanded', 'false');
      root.hidden = false;
      back.hidden = true;
      if (brand) brand.hidden = false;
      if (focus) opener.focus();
      opener = null;
    };

    drawer.querySelectorAll('[data-almila-drawer-open]').forEach(row => {
      row.addEventListener('click', () => {
        const panel = document.getElementById(row.getAttribute('aria-controls'));
        if (!panel) return;
        opener = row;
        row.setAttribute('aria-expanded', 'true');
        title.textContent = panel.dataset.title || '';
        root.hidden = true;
        if (brand) brand.hidden = true;
        back.hidden = false;
        panel.hidden = false;
        panel.scrollTop = 0;
        back.focus();
      });
    });
    back.addEventListener('click', () => showRoot(true));

    const toggle = document.querySelector('.js-menu-toggle-home');
    if (toggle) toggle.addEventListener('click', () => showRoot(false), true);
  }
})();
